import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import { Modal, Button, FormGroup, ControlLabel, FormControl, HelpBlock } from 'react-bootstrap';
import Fa from 'react-fontawesome';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import Autosuggest from 'react-autosuggest';
import { debounce } from 'lodash';
import styles from './styles.css';
import UserAvatar from 'react-user-avatar';
import DateTimeDisplay from '../../../../components/date-time-display';

// Import Actions
import { searchUnassignPIUserOrganizations, requestAssignPIUserOrganization } from '../../AdminActions';

// Import Selectors

class OrganizationAssignButton extends Component {
  constructor() {
    super();
    this.state = {
      showModal: false,
      value: '',
      suggestion: null,
      loading: false,
      suggestions: [],
    };
    this.debouncedLoadSuggestions = debounce(this.loadSuggestions, 700);
    this.onChange = this.onChange.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(this);
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.getSuggestionValue = this.getSuggestionValue.bind(this);
    this.close = this.close.bind(this);
    this.open = this.open.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.renderSuggestion = this.renderSuggestion.bind(this);
    this.renderField = this.renderField.bind(this);
  }

  componentDidMount() {
    this.mounted = true;
  }
  componentWillUnmount() {
    this.mounted = false;
  }

  onChange(event, { newValue }) {
    this.setState({
      value: newValue,
    });
  }

  onBlur() {
    if (this.state.suggestion) {
      const validValue = this.getSuggestionValue(this.state.suggestion);
      if (this.state.value !== validValue) {
        this.setState({ value: '', suggestion: null });
      }
    }
  }

  // Autosuggest will call this function every time
  // you need to update suggestions.
  onSuggestionsFetchRequested({ value }) {
    this.debouncedLoadSuggestions(value);
  }

  // Autosuggest will call this function every time you need to clear suggestions.
  onSuggestionsClearRequested() {
    this.setState({ suggestions: [] });
  }

  onSuggestionSelected(event, { suggestion }) {
    this.props.initialize({
      organization_id: suggestion ? suggestion._id : null,
    });
  }

  getSuggestionValue(suggestion) {
    return suggestion.display_name;
  }

  loadSuggestions(q) {
    const { user } = this.props;
    this.setState({
      loading: true,
      suggestions: [],
    });
    return this.props.dispatch(searchUnassignPIUserOrganizations(user._id, { q }))
      .then(
        (results) => {
          if (q === this.state.value) {
            this.setState({
              suggestions: results.organizations ? results.organizations.items || results.organizations : [],
            });
          }
          this.setState({
            loading: false,
          });
        },
        () => {
          this.setState({
            loading: false,
            suggestions: [],
          });
        },
      );
  }

  close() {
    this.props.reset();
    if (this.mounted) {
      this.setState({ showModal: false, value: '', suggestions: [] });
    }
  }

  open() {
    this.setState({ showModal: true });
  }

  handleSubmit(data) {
    const { user } = this.props;
    return this.props.dispatch(requestAssignPIUserOrganization(user._id, data))
      .then(
        (result) => {
          this.props.onAssign(result.organization);
          this.close();
        },
        (errors) => {
          throw new SubmissionError(errors.data || errors);
        },
      );
  }

  renderSuggestion(suggestion) {
    return (
      <div className="media">
        <div className="media-left">
          <div className={styles.orgPhoto}>
            <UserAvatar borderRadius="2px" name={suggestion.display_name} size="24" />
          </div>
        </div>
        <div className="media-body">
          <h4 className="media-heading">
            {suggestion.display_name}
            <small>&nbsp;(Last synced:&nbsp;
              {suggestion.last_synced_date !== null && <DateTimeDisplay value={suggestion.last_synced_date} format="shortWithTime" />}
              {suggestion.last_synced_date === null && <em className="text-muted">never</em>})
            </small>
          </h4>
        </div>
      </div>
    );
  }

  renderField({
    input, label, type, disabled, meta: { touched, error },
  }) {
    const { submitting } = this.props;
    const validationState = touched && error ? 'error' : null;
    const readOnly = true;
    return (
      <FormGroup validationState={validationState}>
        <div className="hidden">
          <FormControl {...input} readOnly={readOnly} placeholder={label} type={type} disabled={submitting || disabled || readOnly} />
        </div>
        <FormControl.Feedback />
        {touched && error && <HelpBlock>{error}</HelpBlock>}
      </FormGroup>
    );
  }

  renderModal() {
    const { handleSubmit, submitting } = this.props;
    const { value, suggestions } = this.state;
    const inputProps = {
      placeholder: 'Search for organizations',
      value,
      onChange: this.onChange,
      onBlur: this.onBlur,
      className: 'form-control',
      disabled: submitting,
    };
    const theme = {
      container: styles.autocomplete,
      suggestionsContainer: 'dropdown open',
      suggestionsList: 'list-group',
      suggestion: 'list-group-item list-group-item-action',
      suggestionFocused: 'active',
    };
    return (
      <Modal show={this.state.showModal} onHide={this.close}>
        <Modal.Header closeButton>
          <Modal.Title>Assign an organization</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit(this.handleSubmit)}>
          <Modal.Body>
            <FormGroup>
              <ControlLabel>Organization</ControlLabel>
              <Autosuggest
                theme={theme}
                suggestions={suggestions}
                onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                shouldRenderSuggestions={() => true}
                onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                onSuggestionSelected={this.onSuggestionSelected}
                getSuggestionValue={this.getSuggestionValue}
                renderSuggestion={this.renderSuggestion}
                inputProps={inputProps}
              />
            </FormGroup>
            <Field name="organization_id" type="text" component={this.renderField} label="Organization" />
          </Modal.Body>
          <Modal.Footer>
            <Button bsStyle="link" disabled={submitting} onClick={this.close}>Cancel</Button>
            <Button bsStyle="primary" disabled={submitting} type="submit">
              {!submitting && <span><Fa name="plus" /> Assign</span>}
              {submitting && <span><Fa name="spinner" spin /> Assigning</span>}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    );
  }

  render() {
    const { disabled, bsSize } = this.props;
    return (
      <span>
        {this.renderModal()}
        <Button disabled={disabled} onClick={this.open} bsSize={bsSize} block bsStyle="primary">
          <Fa name="plus" /> Assign An Organization
        </Button>
      </span>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps() {
  return {};
}

OrganizationAssignButton.propTypes = {
  initialize: PropTypes.func,
  dispatch: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func,
  reset: PropTypes.func,
  onAssign: PropTypes.func,
  disabled: PropTypes.bool.isRequired,
  user: PropTypes.shape({}).isRequired,
  bsSize: PropTypes.string,
};
OrganizationAssignButton.defaultProps = {
  onAssign: () => {},
  disabled: false,
  bsSize: null,
};


const OrganizationAssignButtonForm = reduxForm({
  form: 'organizationAssign',
})(OrganizationAssignButton);

export default connect(mapStateToProps)(OrganizationAssignButtonForm);
